import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  DollarSign,
  Plus,
  Loader2,
  CheckCircle2,
  Trash2,
  Download,
  FileText,
  Clock,
  Gift,
  AlertCircle,
  TrendingUp,
} from 'lucide-react';
import { cn, localToday } from '@/lib/utils';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', icon: FileText },
  approved: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: CheckCircle2 },
  paid: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: DollarSign },
};

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Create Payroll Run Dialog
function CreateRunDialog({ open, onOpenChange, onSuccess }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    return localToday(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [periodEnd, setPeriodEnd] = useState(() => localToday());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/payroll/runs', { period_start: periodStart, period_end: periodEnd });
      toast.success(t('payroll.runCreated'));
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payroll run');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('payroll.newRun')}</DialogTitle>
          <DialogDescription>{t('payroll.subtitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>{t('payroll.periodStart')}</Label>
              <Input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
                data-testid="payroll-period-start"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('payroll.periodEnd')}</Label>
              <Input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
                data-testid="payroll-period-end"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} data-testid="payroll-create-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('payroll.createRun')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Bonus/Penalty Dialog
function BonusPenaltyDialog({ open, onOpenChange, runId, worker, onSuccess }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [bonus, setBonus] = useState(worker?.bonus || 0);
  const [penalty, setPenalty] = useState(worker?.penalty || 0);
  const [bonusNote, setBonusNote] = useState(worker?.bonus_note || '');
  const [penaltyNote, setPenaltyNote] = useState(worker?.penalty_note || '');

  useEffect(() => {
    if (worker) {
      setBonus(worker.bonus || 0);
      setPenalty(worker.penalty || 0);
      setBonusNote(worker.bonus_note || '');
      setPenaltyNote(worker.penalty_note || '');
    }
  }, [worker]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/payroll/runs/${runId}/bonus-penalty`, {
        worker_id: worker.worker_id,
        bonus: parseFloat(bonus) || 0,
        penalty: parseFloat(penalty) || 0,
        bonus_note: bonusNote || null,
        penalty_note: penaltyNote || null
      });
      toast.success(t('payroll.bonusAdded'));
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bonus/penalty');
    } finally {
      setLoading(false);
    }
  };

  if (!worker) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('payroll.addBonus')}</DialogTitle>
          <DialogDescription>{worker.worker_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-green-600">{t('payroll.bonus')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={bonus}
                  onChange={(e) => setBonus(e.target.value)}
                  placeholder="0.00"
                  data-testid="bonus-amount"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-red-600">{t('payroll.penalty')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={penalty}
                  onChange={(e) => setPenalty(e.target.value)}
                  placeholder="0.00"
                  data-testid="penalty-amount"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('payroll.bonusNote')}</Label>
              <Input
                value={bonusNote}
                onChange={(e) => setBonusNote(e.target.value)}
                placeholder={t('payroll.bonusNote')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('payroll.penaltyNote')}</Label>
              <Input
                value={penaltyNote}
                onChange={(e) => setPenaltyNote(e.target.value)}
                placeholder={t('payroll.penaltyNote')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Payroll Run Card
function PayrollRunCard({ run, onUpdate, expanded, onToggle }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [actionLoading, setActionLoading] = useState(false);
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  const statusCfg = STATUS_CONFIG[run.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await api.post(`/payroll/runs/${run.id}/approve`);
      toast.success(t('payroll.runApproved'));
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try {
      await api.post(`/payroll/runs/${run.id}/mark-paid`);
      toast.success(t('payroll.runPaid'));
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setActionLoading(false);
    }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/payroll/runs/${run.id}`);
      toast.success(t('payroll.runDeleted'));
      setDeleteOpen(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/payroll/runs/${run.id}/export.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${run.period_start}_${run.period_end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('time.exportSuccess'));
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(18);
      doc.text('Payroll Report', 14, 22);
      
      // Period
      doc.setFontSize(11);
      doc.text(`Period: ${formatDate(run.period_start)} - ${formatDate(run.period_end)}`, 14, 32);
      doc.text(`Status: ${run.status}`, 14, 38);
      doc.text(`Total: $${run.total_amount?.toFixed(2) || 0}`, 14, 44);
      
      // Table data
      const tableData = run.lines?.map(line => [
        line.worker_name || 'Unknown',
        (line.hours_worked || 0).toFixed(1),
        line.orders_completed || 0,
        `$${(line.gross_amount || 0).toFixed(2)}`,
        line.bonus > 0 ? `+$${line.bonus.toFixed(2)}` : '-',
        line.penalty > 0 ? `-$${line.penalty.toFixed(2)}` : '-',
        `$${(line.net_amount || 0).toFixed(2)}`
      ]) || [];
      
      // Generate table
      autoTable(doc, {
        startY: 52,
        head: [['Worker', 'Hours', 'Orders', 'Gross', 'Bonus', 'Penalty', 'Net']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [79, 70, 229] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 40 },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right', fontStyle: 'bold' }
        }
      });
      
      // Footer
      const finalY = doc.lastAutoTable.finalY || 100;
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, finalY + 10);
      
      // Save
      doc.save(`payroll_${run.period_start}_${run.period_end}.pdf`);
      toast.success(t('time.exportSuccess'));
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('PDF export failed');
    }
  };

  const openBonusDialog = (worker) => {
    setSelectedWorker(worker);
    setBonusDialogOpen(true);
  };

  return (
    <>
      <Accordion type="single" collapsible value={expanded ? run.id : ''} onValueChange={(val) => { if (val) { onToggle(); } else { onToggle(); } }}>
        <AccordionItem value={run.id} className="border rounded-lg cgc-card mb-4">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center justify-between w-full pr-4">
              <div className="flex items-center gap-4">
                <Badge className={cn("text-xs", statusCfg.color)}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {t(`payroll.${run.status}`)}
                </Badge>
                <span className="font-medium">
                  {formatDate(run.period_start)} — {formatDate(run.period_end)}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-accent tabular-nums">
                  ${run.total_amount?.toFixed(2)}
                </span>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            {/* Actions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {run.status === 'draft' && (
                <>
                  <Button size="sm" onClick={handleApprove} disabled={actionLoading} data-testid={`approve-${run.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    {t('payroll.approve')}
                  </Button>
                </>
              )}
              {run.status === 'approved' && (
                <Button size="sm" onClick={handleMarkPaid} disabled={actionLoading}>
                  <DollarSign className="h-4 w-4 mr-1" />
                  {t('payroll.markPaid')}
                </Button>
              )}
              {run.status !== 'paid' && (
                <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" disabled={actionLoading} data-testid={`delete-payroll-${run.id}`}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('payroll.delete')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Удалить зарплатный отчёт?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Отчёт за {run.period_start} — {run.period_end} будет удалён. Это действие нельзя отменить.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Отмена</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        Удалить
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button size="sm" variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-1" />
                {t('payroll.export')}
              </Button>
              <Button size="sm" variant="outline" onClick={handleExportPDF}>
                <FileText className="h-4 w-4 mr-1" />
                PDF
              </Button>
            </div>

            {/* Lines Table */}
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('payroll.worker')}</TableHead>
                    <TableHead className="text-center">{t('payroll.rate')}</TableHead>
                    <TableHead className="text-center">{t('payroll.hoursWorked')}</TableHead>
                    <TableHead className="text-center">{t('payroll.ordersCompleted')}</TableHead>
                    <TableHead className="text-right">{t('payroll.gross')}</TableHead>
                    <TableHead className="text-center">{t('payroll.bonus')}</TableHead>
                    <TableHead className="text-center">{t('payroll.penalty')}</TableHead>
                    <TableHead className="text-right">{t('payroll.net')}</TableHead>
                    {run.status === 'draft' && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {run.lines?.map((line, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow>
                        <TableCell className="font-medium">
                          {line.worker_name || 'Unknown'}
                          {line.rate_changes && line.rate_changes.length > 0 && (
                            <div className="mt-1">
                              {line.rate_changes.map((ch, ci) => (
                                <span key={ci} className="inline-block text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 mr-1">
                                  {ch.date}: {ch.old_type === 'hourly' ? `$${ch.old_rate}/ч` : `${ch.old_rate}%`} → {ch.new_type === 'hourly' ? `$${ch.new_rate}/ч` : `${ch.new_rate}%`}
                                </span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {line.payment_type === 'percentage' ? (
                            <span className="text-purple-600 dark:text-purple-400 font-semibold">{line.percentage_rate}%</span>
                          ) : (
                            <span className="text-green-600 dark:text-green-400 font-semibold">${line.hourly_rate}/ч</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{line.hours_worked?.toFixed(1) || 0}</TableCell>
                        <TableCell className="text-center">{line.orders_completed || 0}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          ${line.gross_amount?.toFixed(2) || 0}
                          {line.payment_type === 'percentage' && line.percentage_revenue > 0 && (
                            <div className="text-[11px] text-muted-foreground">
                              {line.percentage_rate}% от ${line.percentage_revenue?.toFixed(0)}
                              <span className="text-amber-500"> (−GST 5%)</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {line.bonus > 0 && (
                            <span className="text-green-600 tabular-nums" title={line.bonus_note}>
                              +${line.bonus.toFixed(2)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {line.penalty > 0 && (
                            <span className="text-red-600 tabular-nums" title={line.penalty_note}>
                              -${line.penalty.toFixed(2)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-bold tabular-nums">${line.net_amount?.toFixed(2) || 0}</TableCell>
                        {run.status === 'draft' && (
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openBonusDialog(line)}
                              data-testid={`bonus-${line.worker_id}`}
                            >
                              <Gift className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {/* Sub-period breakdown rows */}
                      {line.sub_periods && line.sub_periods.length > 0 && line.sub_periods.map((sp, si) => (
                        <TableRow key={`sp-${idx}-${si}`} className="bg-muted/30 text-xs">
                          <TableCell className="pl-8 text-muted-foreground">
                            ↳ {sp.start} — {sp.end}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {sp.payment_type === 'percentage' ? `${sp.rate}%` : `$${sp.rate}/ч`}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground tabular-nums">
                            {sp.hours?.toFixed(1) || '—'}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {sp.payment_type === 'percentage' ? `$${sp.revenue?.toFixed(0)}` : '—'}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground tabular-nums">
                            ${sp.amount?.toFixed(2)}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          {run.status === 'draft' && <TableCell></TableCell>}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <BonusPenaltyDialog
        open={bonusDialogOpen}
        onOpenChange={setBonusDialogOpen}
        runId={run.id}
        worker={selectedWorker}
        onSuccess={onUpdate}
      />
    </>
  );
}

// Main Payroll Page
// Company Profit & Co-founder Shares Section (admin only)
function CompanyProfitSection({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    return localToday(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [periodEnd, setPeriodEnd] = useState(() => localToday());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/company-profit?period_start=${periodStart}&period_end=${periodEnd}`);
      setData(res.data);
    } catch {
      toast.error('Не удалось загрузить данные по прибыли');
    } finally {
      setLoading(false);
    }
  }, [api, periodStart, periodEnd]);

  const Row = ({ label, value, className = '', sub }) => (
    <div className={`flex justify-between items-start py-2 border-b border-border/50 last:border-0 ${className}`}>
      <div>
        <span className="text-sm">{label}</span>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
      <span className={`font-mono font-semibold text-sm ${className}`}>{value}</span>
    </div>
  );

  return (
    <Card className="cgc-card border-2 border-accent/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-accent" />
          Прибыль компании и доли соучредителей
          <span className="text-xs font-normal text-muted-foreground ml-auto">🔒 Только администратор</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Период с</Label>
            <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">по</Label>
            <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-36" />
          </div>
          <Button size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Рассчитать'}
          </Button>
        </div>

        {data && (
          <div className="space-y-4">
            {/* Revenue breakdown */}
            <div className="p-4 rounded-lg bg-muted/30 space-y-0">
              <Row label="Выручка (Gross)" value={`$${data.gross_revenue?.toFixed(2)}`} />
              <Row label={`GST ${data.gst_rate}%`} value={`−$${data.total_gst?.toFixed(2)}`} className="text-amber-600" />
              <Row label="Расходы по заказам" value={`−$${data.total_expenses?.toFixed(2)}`} className="text-amber-600" />
              <Row label="ФОТ работники" value={`−$${data.worker_payroll?.toFixed(2)}`} className="text-amber-600" />
              <Row label="ФОТ менеджеры" value={`−$${data.manager_payroll?.toFixed(2)}`} className="text-amber-600" />
              <Row
                label="Прибыль до налогов"
                value={`$${data.profit_before_tax?.toFixed(2)}`}
                className="font-bold text-foreground border-t-2 pt-3 mt-1"
              />
              <Row
                label={`Корпоративный налог (${data.corporate_tax_rate}%)`}
                value={`−$${data.corporate_tax?.toFixed(2)}`}
                className="text-amber-600"
                sub="Ориентировочно, уточните у бухгалтера"
              />
            </div>

            {/* Net profit */}
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex justify-between items-center">
                <p className="font-semibold">Чистая прибыль</p>
                <p className="text-2xl font-bold text-green-600">${data.net_profit?.toFixed(2)}</p>
              </div>
            </div>

            {/* Co-founder shares */}
            {data.cofounders?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Доли соучредителей</p>
                <div className="grid gap-2">
                  {data.cofounders.map(cf => (
                    <div key={cf.name} className="flex justify-between items-center p-3 rounded-lg border bg-muted/20">
                      <div>
                        <p className="font-medium">{cf.name}</p>
                        <p className="text-xs text-muted-foreground">{cf.share_percent}%</p>
                      </div>
                      <p className="text-lg font-bold tabular-nums">${cf.amount?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground italic">{data.note}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// My Manager Salary (for managers viewing their own)
function MyManagerSalary({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    return localToday(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [periodEnd, setPeriodEnd] = useState(() => localToday());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/manager-salary/me?period_start=${periodStart}&period_end=${periodEnd}`);
      setData(res.data);
    } catch {
      // No profile set yet — show empty state, don't error
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, periodStart, periodEnd]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="cgc-card border-accent/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-accent" />
          Моя зарплата
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">С</Label>
            <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">По</Label>
            <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-36" />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Обновить'}
          </Button>
        </div>

        {!data ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Ставка ещё не настроена администратором
          </div>
        ) : (
          <div className="space-y-3">
            {/* Total */}
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">Итого к выплате</p>
                <p className="text-3xl font-bold text-green-600 tabular-nums">${data.total_to_pay?.toFixed(2)}</p>
              </div>
              {data.bonus_pending > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  + ожидается ${data.bonus_pending?.toFixed(2)} по незавершённым заказам
                </p>
              )}
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/40 space-y-1">
                <p className="text-xs text-muted-foreground">Базовая оплата</p>
                <p className="text-xl font-bold tabular-nums">${data.base_pay?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">
                  {data.base_payment_type === 'hourly'
                    ? `${data.hours_worked?.toFixed(1)}ч × $${data.hourly_rate}/ч`
                    : 'Фиксированный оклад'}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-muted/40 space-y-1">
                <p className="text-xs text-muted-foreground">Бонусы за заказы</p>
                <p className="text-xl font-bold text-green-600 tabular-nums">${data.bonus_earned?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">
                  {data.orders_done} выполн.
                  {data.bonus_type === 'percentage'
                    ? ` × ${data.bonus_percentage}%`
                    : ` × $${data.bonus_fixed}`}
                </p>
              </div>
            </div>

            {/* Pending bonus */}
            {data.orders_pending_count > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">Ожидаемые бонусы</p>
                    <p className="text-xs text-muted-foreground">{data.orders_pending_count} заказов ещё не завершены</p>
                  </div>
                  <p className="text-lg font-bold text-amber-600">${data.bonus_pending?.toFixed(2)}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Manager Payroll Summary Section
function ManagerPayrollSummary({ api }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    return localToday(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [periodEnd, setPeriodEnd] = useState(() => localToday());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/manager-summary?period_start=${periodStart}&period_end=${periodEnd}`);
      setData(res.data);
    } catch {
      toast.error('Не удалось загрузить данные менеджеров');
    } finally {
      setLoading(false);
    }
  }, [api, periodStart, periodEnd]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="cgc-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-accent" />
          Зарплата менеджеров
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Период с</Label>
            <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">по</Label>
            <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-36" />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Обновить'}
          </Button>
        </div>

        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Нет данных по менеджерам</p>
        ) : (
          <div className="space-y-3">
            {data.map(mgr => (
              <div key={mgr.manager_user_id} className="p-4 rounded-lg border bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{mgr.manager_name || 'Менеджер'}</p>
                  <p className="text-lg font-bold text-green-600">${mgr.total_to_pay?.toFixed(2)}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Базовая оплата</p>
                    <p className="font-medium">
                      ${mgr.base_pay?.toFixed(2)}
                      <span className="text-xs text-muted-foreground ml-1">
                        {mgr.base_payment_type === 'hourly' ? `(${mgr.hours_worked?.toFixed(1)}ч × $${mgr.hourly_rate})` : '(оклад)'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Заказов добавлено</p>
                    <p className="font-medium">{(mgr.orders_done || 0) + (mgr.orders_pending || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Бонус выплачен</p>
                    <p className="font-medium text-green-600">${mgr.bonus_earned?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Бонус ожидается</p>
                    <p className="font-medium text-amber-500">${mgr.bonus_pending?.toFixed(2)}</p>
                  </div>
                </div>
                {mgr.bonus_type === 'percentage' ? (
                  <p className="text-xs text-muted-foreground">Бонус: {mgr.bonus_percentage}% от суммы заказа</p>
                ) : mgr.bonus_fixed > 0 ? (
                  <p className="text-xs text-muted-foreground">Бонус: ${mgr.bonus_fixed} за каждый выполненный заказ</p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PayrollPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/runs?limit=20');
      setRuns(res.data);
      // Auto-expand first draft if exists
      const firstDraft = res.data.find(r => r.status === 'draft');
      if (firstDraft && !expandedRunId) {
        setExpandedRunId(firstDraft.id);
      }
    } catch (err) {
      console.error('Failed to fetch payroll runs:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Stats
  const draftCount = runs.filter(r => r.status === 'draft').length;
  const totalPending = runs.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.total_amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('payroll.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('payroll.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="new-payroll-btn">
          <Plus className="mr-2 h-4 w-4" />
          {t('payroll.newRun')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="cgc-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-slate-500" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">{draftCount}</p>
                <p className="text-xs text-muted-foreground">{t('payroll.draft')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cgc-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">${totalPending.toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">{t('payroll.approved')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payroll Runs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : runs.length === 0 ? (
        <Card className="cgc-card">
          <CardContent className="p-12 text-center">
            <DollarSign className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">{t('payroll.noRuns')}</p>
            <Button variant="link" onClick={() => setCreateDialogOpen(true)} className="mt-2">
              {t('payroll.createFirst')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div>
          {runs.map(run => (
            <PayrollRunCard
              key={run.id}
              run={run}
              onUpdate={fetchRuns}
              expanded={expandedRunId === run.id}
              onToggle={() => setExpandedRunId(expandedRunId === run.id ? null : run.id)}
            />
          ))}
        </div>
      )}

      {/* My Salary (for managers) */}
      {user?.role === 'manager' && (
        <MyManagerSalary api={api} />
      )}

      {/* Manager Payroll Summary (admin only — all managers) */}
      {user?.role === 'admin' && (
        <ManagerPayrollSummary api={api} />
      )}

      {/* Company Profit & Co-founder Shares (admin only) */}
      {user?.role === 'admin' && (
        <CompanyProfitSection api={api} />
      )}

      {/* Create Dialog */}
      <CreateRunDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={fetchRuns}
      />
    </div>
  );
}
